// Dispatch only to controls from a fresh observation of the ordinary page.
// Existing React handlers retain validation, approval, persistence and audit ownership.
const appUiControlApi = (() => {
  const create = (deps: any) => {
    const doc: Document = deps.document || document
    let observed: any = null, revision = 0, query = '', offset = 0, disposed = false
    const used = new Set<string>()
    const identity = new WeakMap<Element, number>()
    let nextIdentity = 0
    const nodeId = (element: Element) => {
      if (!identity.has(element)) identity.set(element, ++nextIdentity)
      return identity.get(element)
    }
    const read = () => {
      const result = window.PlummeAppUiObservation.collect(doc, query, offset)
      const rows = result.rows.map((item: any) => {
        const row = { ...item, controlId: `control-${nodeId(item.element)}` }
        delete row.element
        return row
      })
      const ui = { route: deps.getRoute(), dialog: result.dialog, text: result.text, statuses: result.statuses || [], controls: rows,
        query, offset, total: result.total, nextOffset: offset + rows.length < result.total ? offset + rows.length : null }
      const values = result.rows.map(({ element }: any) => [element.value, element.checked, element.getAttribute('aria-checked')])
      return { result, ui, key: JSON.stringify({ ...ui, statuses: [], values }) }
    }
    const snapshot = () => {
      if (disposed || !deps.isCurrent()) return null
      const current = read()
      if (!observed || current.key !== observed.key) {
        observed = { ...current, observationId: `surface-${++revision}` }
      } else observed.ui = current.ui
      return { ...observed.ui, observationId: observed.observationId }
    }
    const fail = (key: string): never => { throw new Error(`appControl.${key}`) }
    const perform = async (step: any, question: string) => {
      if (disposed || !deps.isCurrent()) fail('changed')
      if (step.action === 'inspect') {
        query = String(step.query || '').slice(0, 100)
        offset = Number.isInteger(step.offset) && step.offset >= 0 && step.offset <= 10000 ? step.offset : 0
        observed = null
        return { action: 'inspect', result: 'observed', confirmed: false }
      }
      if (!['click', 'fill', 'select', 'check'].includes(step.action)) fail('unsupported')
      const quote = String(step.authorizationQuote || '').trim()
      if (quote.length < 4 || !question.includes(quote)) fail('authorization')
      if (!observed || step.observationId !== observed.observationId || read().key !== observed.key) fail('changed')
      const index = observed.ui.controls.findIndex((row: any) => row.controlId === step.controlId)
      if (index < 0) fail('changed')
      const element: any = observed.result.rows[index].element
      const row = observed.ui.controls[index]
      if (!window.PlummeAppUiObservation.visible(element) || row.disabled || row.readOnly || row.requiresUser) fail('manual')
      if (row.external) fail('manual')
      if (element.tagName === 'A' && !['http:', 'https:'].includes(new URL(element.href, doc.baseURI).protocol)) fail('manual')
      const fingerprint = JSON.stringify([step.controlId, step.action, step.value, step.checked])
      const semanticFingerprint = JSON.stringify([deps.getRoute(), row.label, row.icon, row.context, step.action, step.value, step.checked])
      if (used.has(fingerprint) || used.has(semanticFingerprint)) fail('repeated')
      if (step.action === 'click' && ['checkbox', 'radio', 'switch'].includes(row.type)) fail('unsupported')
      if (step.action === 'fill' && (!['INPUT', 'TEXTAREA'].includes(element.tagName) || ['button', 'submit', 'reset', 'checkbox', 'radio', 'file', 'password', 'hidden'].includes(row.type))) fail('unsupported')
      if (step.action === 'select' && (element.tagName !== 'SELECT' || !row.options.some((option: any) => !option.disabled && option.value === step.value))) fail('option')
      if (step.action === 'check' && (!['checkbox', 'radio', 'switch'].includes(row.type) || typeof step.checked !== 'boolean')) fail('unsupported')
      if (['fill', 'select'].includes(step.action) && (typeof step.value !== 'string' || step.value.length > 4000)) fail('unsupported')
      // Consume before dispatch. An exception or uncertain asynchronous outcome cannot be retried.
      used.add(fingerprint)
      used.add(semanticFingerprint)
      const previousKey = observed.key
      observed = null
      element.scrollIntoView?.({ block: 'nearest' })
      if (step.action === 'click') element.click()
      else if (step.action === 'check') {
        if (row.type === 'radio' && !step.checked) fail('unsupported')
        if (row.checked !== step.checked) element.click()
      } else {
        const view = doc.defaultView!
        const prototype = element.tagName === 'SELECT' ? view.HTMLSelectElement.prototype
          : element.tagName === 'TEXTAREA' ? view.HTMLTextAreaElement.prototype : view.HTMLInputElement.prototype
        Object.getOwnPropertyDescriptor(prototype, 'value')!.set!.call(element, step.value)
        element.dispatchEvent(new view.Event('input', { bubbles: true }))
        element.dispatchEvent(new view.Event('change', { bubbles: true }))
        element.dispatchEvent(new view.FocusEvent('focusout', { bubbles: true }))
      }
      if (deps.settle) await deps.settle()
      else {
        // React commits and animated/portal dialogs may appear after the event returns.
        // Wait for a changed, stable rendered surface; a timeout is not evidence of saving.
        let lastKey = previousKey, stable = 0
        for (let attempt = 0; attempt < 30 && !disposed && deps.isCurrent(); attempt++) {
          await new Promise((resolve) => setTimeout(resolve, 100))
          const key = read().key
          stable = key !== previousKey && key === lastKey ? stable + 1 : 0
          lastKey = key
          if (stable >= 2) break
        }
      }
      if (disposed || !deps.isCurrent()) fail('changed')
      return { action: step.action, control: row.label, result: 'event_dispatched', confirmed: false,
        requiresObservation: true }
    }
    return { snapshot, perform, beginRequest: () => { used.clear(); observed = null; query = ''; offset = 0 }, dispose: () => { disposed = true; observed = null; used.clear() } }
  }
  return { create }
})()
Object.assign(globalThis, { PlummeAppUiControl: appUiControlApi })
